import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useBreadcrumb } from '../../../context/BreadcrumbContext';
import {
  Box,
  Grid,
  Typography,
  FormLabel,
  FormGroup,
  TableRow,
  IconButton,
  Tooltip
} from '@mui/material';

import CustomTooltip from '../../../components/common/CustomTooltip';

import TotalShiftsIcon from '../../../assets/facility/total-shifts.svg'
import UpcomingIcon from '../../../assets/facility/upcoming-icon.svg'
import CompletedIcon from '../../../assets/facility/completed-icon.svg'
import CancelledIcon from '../../../assets/facility/cancelled-icon.svg'

import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ClearIcon from '@mui/icons-material/Clear';
import CancelIcon from '@mui/icons-material/Cancel';
import apiCall from '../../../utils/api';
import { API_URL } from '../../../utils/env';
import { AppDispatch } from '../../../store';
import FacilityProfileTab from '../../../components/FacilityProfileTab';
import DocsIcon from '../../../assets/main/docs-icon.svg'
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import DownloadIcon from '@mui/icons-material/Download';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { getFileNameFromPath, truncateLast } from '../../../helpers';
import PageCard from '../../../components/PageCard';
import CardTitle from '../../../components/CardTitle'

import {
  BasicDetailItemContainer,
  CustomCard,
  DocsContainer,
  DateContainer,
  DateTextField,
  CustomTab,
  CustomTabPanel,
  SummaryCard,
  SummaryCardsContainer,
  SummaryContent,
  SummaryIconWrapper,
  SummaryTitle,
  SummaryValue,
  StatusTag,
  StyledTable,
  StyledTableBody,
  StyledTableCell,
  StyledTableContainer,
  PaginationFooter
} from './styles';

// redux
import {
  fetchFacilityDetails,
  selectFacilityDetails,
  IUploadedDocument as IFacilityUploadedDocument,
  updateFacilityDocumentDates,
  fetchFacilityShiftStatics,
  selectFacilityShiftStatics,
  updateFacilityStatus,
  selectUpdateFacilityStatusLoading,
  selectUpdateFacilityStatusError,
  acceptRejectFacilityDocument,
  selectAcceptRejectFacilityDocumentLoading
} from '../../../modules/facility';

import {
  fetchFacilityShiftList,
  selectFacilityShiftLists,
  selectFacilityShiftsListsLoading,
  selectFacilityShiftsError,
  selectFacilityShiftsCount
} from '../../../modules/shift';

import AlertPopup from '../../../components/common/AlertPopup';
import ConfirmationDialog from '../../../components/common/ConfirmationDialog';
import DocumentPreviewPopup from '../../../components/common/DocumentPreviewPopup';
import ActionPopup from '../../../components/common/ActionPopup';
import CustomTableHeader from "../../../components/CustomHeader";
import CustomPagination from '../../../components/CustomPagination';
import { TabContext, TabList } from '@mui/lab';
import { IShift } from '@/modules/shift';


type Order = 'asc' | 'desc';

interface Column {
  id: keyof IShift | string;
  label: string;
  numeric?: boolean;
  disableSorting?: boolean;
}

const COLUMN_MAPPING: { [key: string]: keyof IShift | string } = {
  'ID No': 'shift_id',
  'Date': 'start_date_time',
  'Shift Start Time': 'start_date_time',
  'Shift End Time': 'end_date_time',
  'Role': 'position_role',
  'Required': 'workers_required',
  'Assigned': 'shift_assignee_count',
  'Worker Assigned': 'assigned_workers',
  'Status': 'status',
};

// Define the columns structure needed by CustomTableHeader
const FACILITY_SHIFT_COLUMNS: Column[] = Object.entries(COLUMN_MAPPING).map(([label, id]) => ({
  id: id,
  label: label,
  numeric: id === 'shift_id',
  disableSorting: !(id === 'title' || id === 'shift_id'),
}));


interface IFacilityDetailsUploadedDocument extends IFacilityUploadedDocument {
  issuedDate: dayjs.Dayjs | null;
  expiresDate: dayjs.Dayjs | null;
}

const FacilityDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { setPath, setIsShow } = useBreadcrumb();

  const dispatch: AppDispatch = useDispatch();
  const facilityDetails = useSelector(selectFacilityDetails);
  const facilityShiftStatics = useSelector(selectFacilityShiftStatics);
  const facilityShifts = useSelector(selectFacilityShiftLists);
  const facilityShiftsLoading = useSelector(selectFacilityShiftsListsLoading);
  const facilityShiftsError = useSelector(selectFacilityShiftsError);
  const facilityShiftsCount = useSelector(selectFacilityShiftsCount);
  const updateStatusLoading = useSelector(selectUpdateFacilityStatusLoading);
  const updateStatusError = useSelector(selectUpdateFacilityStatusError);
  const documentStatusUpdateLoading = useSelector(selectAcceptRejectFacilityDocumentLoading);

  const [uploadedDocuments, setUploadedDocuments] = useState<IFacilityDetailsUploadedDocument[]>([])
  const [facilityId, setFacilityId] = useState<number>(0)
  const [alert, setAlert] = useState<{ message: string, severity: 'success' | 'error' } | null>(null);
  const [selectedTab, setSelectedTab] = useState<string>('facility_details');
  const [confirmation, setConfirmation] = useState<{ open: boolean; title: string; message: string; onConfirm: (() => void) | null }>({
    open: false, title: '', message: '', onConfirm: null
  });
  const [actionToPerform, setActionToPerform] = useState<string | null>(null); // Removed
  const [statusUpdateCompleted, setStatusUpdateCompleted] = useState(false);


  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof IShift | string>('title');


  const [showPreviewPopup, setShowPreviewPopup] = useState<boolean>(false); // State for DocumentPreviewPopup visibility
  const [previewFileUrl, setPreviewFileUrl] = useState<string>(''); // State for the URL of the file to preview
  const [previewFileType, setPreviewFileType] = useState<'image' | 'pdf' | 'other'>('other'); // State for the type of file to preview
  const [previewFileName, setPreviewFileName] = useState<string>(''); // State for the name of the file to preview

  const [showRejectPopup, setShowRejectPopup] = useState<boolean>(false);
  const [documentToReject, setDocumentToReject] = useState<IFacilityDetailsUploadedDocument | null>(null);
  const [showAcceptPopup, setShowAcceptPopup] = useState<boolean>(false);
  const [documentToAccept, setDocumentToAccept] = useState<IFacilityDetailsUploadedDocument | null>(null);
  const [showSuspendPopup, setShowSuspendPopup] = useState<boolean>(false);

  useEffect(() => {
    setPath([
      { title: 'Management', path: '/' },
      { title: 'Facility', path: '/facilities' },
      { title: 'Details', path: '' },
    ]);
    setIsShow(true);
  }, [setPath, setIsShow]);

  useEffect(() => {
    try {
      if (id) {
        const facilityId = parseInt(id)
        setFacilityId(facilityId)
        dispatch(fetchFacilityDetails(facilityId) as any);
        dispatch(fetchFacilityShiftStatics(facilityId) as any);
      }
    } catch (err) {
      console.log(err);
    }
  }, [dispatch, id])

  useEffect(() => {
    if (facilityId) {
      dispatch(fetchFacilityShiftList({ facilityId, page, pageSize: rowsPerPage, orderBy: orderBy as string, order }));
    }
  }, [dispatch, facilityId, page, order, orderBy, rowsPerPage]);

  useEffect(() => {
    if (facilityDetails) {

      if (facilityDetails.uploaded_documents) {
        const documents = facilityDetails.uploaded_documents.map(doc => ({
          ...doc,
          issuedDate: doc.document_issued_date ? dayjs(doc.document_issued_date) : null,
          expiresDate: doc.document_expiring_date ? dayjs(doc.document_expiring_date) : null,
        }));
        setUploadedDocuments(documents);
      } else {
        setUploadedDocuments([]);
      }

    }
  }, [facilityDetails])

  useEffect(() => {
    if (statusUpdateCompleted && !updateStatusLoading) {
      if (updateStatusError) {
        setAlert({ message: `Error updating facility status: ${updateStatusError}`, severity: 'error' });
      } else {
        // The action that was performed needs to be passed through the alert state or derived
        // For now, we'll use a generic success message.
        setAlert({ message: `Facility ${actionToPerform} successfully!`, severity: 'success' });
        dispatch(fetchFacilityDetails(facilityId) as any); // Refresh facility details
      }
      setStatusUpdateCompleted(false); // Reset after handling
      // setActionToPerform(null); // Removed
    }
  }, [statusUpdateCompleted, updateStatusLoading, updateStatusError, dispatch, facilityId, actionToPerform]);


  const handleDocumentDateChange = async (index: number, field: 'issuedDate' | 'expiresDate', newValue: dayjs.Dayjs | null) => {
    const updatedDocuments = [...uploadedDocuments];
    updatedDocuments[index] = {
      ...updatedDocuments[index],
      [field]: newValue,
    };
    setUploadedDocuments(updatedDocuments);

    const documentToUpdate = updatedDocuments[index];
    if (documentToUpdate.document_id) {
      const payload: { documentId: number; document_issued_date?: string; document_expiring_date?: string } = {
        documentId: documentToUpdate.document_id,
      };

      if (field === 'issuedDate') {
        payload.document_issued_date = newValue ? newValue.format('YYYY-MM-DD') : undefined;
      } else if (field === 'expiresDate') {
        payload.document_expiring_date = newValue ? newValue.format('YYYY-MM-DD') : undefined;
      }

      const result = await dispatch(updateFacilityDocumentDates(payload) as any);

      if (updateFacilityDocumentDates.fulfilled.match(result)) {
        setAlert({ message: 'Document dates updated successfully!', severity: 'success' });
        dispatch(fetchFacilityDetails(facilityId) as any); // Refresh facility details
      } else {
        setAlert({ message: (result.payload as string) || 'Failed to update document dates.', severity: 'error' });
      }
    }
  };

  // Removed handleSave and worker-specific update functions

  interface BasicDetailItemProps {
    title: string;
    value: string | undefined | null;
  }

  const BasicDetailItem = ({ title, value }: BasicDetailItemProps) => (
    <BasicDetailItemContainer>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 0.5, fontWeight: 600 }}
      >
        {title}
      </Typography>
      <Typography
        variant="body1"
        color="text.primary"
        fontWeight="medium"
      >
        {value}
      </Typography>
    </BasicDetailItemContainer>
  )

  const getFileType = (url: string): 'image' | 'pdf' | 'other' => {
    const extension = url.split('.').pop()?.toLowerCase();
    if (extension && ['jpg', 'jpeg', 'png', 'gif'].includes(extension)) {
      return 'image';
    }
    if (extension === 'pdf') {
      return 'pdf';
    }
    return 'other';
  };

  const handleDownload = async (url: string, fileName: string) => {
    try {
      let blob: Blob;
      if (url.startsWith('http') || url.startsWith('blob:') || url.startsWith('data:')) {
        const response = await fetch(url);
        if (!response.ok) throw new Error('Network response was not ok');
        blob = await response.blob();
      } else {
        const response = await apiCall<Blob>(
          'get',
          `/api/fetch-s3-file/?file_path=${encodeURIComponent(url)}&file_format=blob`,
          undefined,
          undefined,
          'blob'
        );
        blob = response.data;
      }

      if (!(blob instanceof Blob)) {
        throw new Error('Failed to get a valid file');
      }

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
      const fallbackUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
      window.open(fallbackUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleActionButtonClick = (action: string | undefined) => {
    // setActionToPerform(action); // Removed
    let title = '';
    let message = '';
    switch (action) {
      case 'approved':
        title = 'Approve Facility';
        message = 'Are you sure you want to approve this facility?';
        break;
      case 'rejected':
        title = 'Reject Facility';
        message = 'Are you sure you want to reject this facility?';
        break;
      case 'suspended':
        setShowSuspendPopup(true);
        return;
      case 'activated':
        title = 'Activate Facility';
        message = 'Are you sure you want to activate this facility?';
        break;
      default:
        return;
    }
    setConfirmation({
      open: true,
      title,
      message,
      onConfirm: () => handleConfirmStatusUpdate(action as string), // Pass action directly
    });
  };

  const handleConfirmStatusUpdate = (action: string, reason?: string) => {
    if (facilityId) {
      dispatch(updateFacilityStatus({ facilityId, action: action, reason }) as any);
      setConfirmation({ open: false, title: '', message: '', onConfirm: null }); // Close dialog
      setShowSuspendPopup(false);
      setStatusUpdateCompleted(true);
      setActionToPerform(action)
    }
  };

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string,
  ) => {
    const propertyKey = property as keyof IShift | string;

    const columnDef = FACILITY_SHIFT_COLUMNS.find(col => col.id === property);
    if (columnDef?.disableSorting) {
      return;
    }

    const isAsc = orderBy === propertyKey && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(propertyKey);
  };

  return (
    <Box>
      {alert && <AlertPopup message={alert.message} severity={alert.severity} open={!!alert} onClose={() => setAlert(null)} />}
      {facilityDetails && // Changed from workerDetails
        <PageCard padding={4} backgroundRequired={true}>
          <FacilityProfileTab
            facilityName={facilityDetails.facility_name}
            id={facilityDetails.facility_id}
            status={facilityDetails.status}
            onClickButton={handleActionButtonClick}
            sx={{ mb: 4 }}
          />

          <TabContext value={selectedTab}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <TabList onChange={handleChange} aria-label="lab API tabs example">
                <CustomTab label="Facility Details" value="facility_details" />
                <CustomTab label="Facility-Created Shifts" value="created_shifts" />
              </TabList>
            </Box>
            <CustomTabPanel value="created_shifts">

              <Box>
                <SummaryCardsContainer>
                  <SummaryCard>
                    <SummaryContent>
                      <SummaryTitle>Total Shifts Created</SummaryTitle>
                      <SummaryValue>
                        {facilityShiftStatics?.total_shifts || 0}
                        <span className="unit-text">shifts</span>
                      </SummaryValue>
                    </SummaryContent>
                    <SummaryIconWrapper colorName="warning">
                      <img src={TotalShiftsIcon} alt="Total Shifts Created" />
                    </SummaryIconWrapper>
                  </SummaryCard>

                  <SummaryCard>
                    <SummaryContent>
                      <SummaryTitle>Upcoming</SummaryTitle>
                      <SummaryValue>
                        {facilityShiftStatics?.upcoming_shifts || 0}
                        <span className="unit-text">shifts</span>
                      </SummaryValue>
                    </SummaryContent>
                    <SummaryIconWrapper colorName="info">
                      <img src={UpcomingIcon} alt="Upcoming Shifts" />
                    </SummaryIconWrapper>
                  </SummaryCard>

                  <SummaryCard>
                    <SummaryContent>
                      <SummaryTitle>Completed</SummaryTitle>
                      <SummaryValue>
                        {facilityShiftStatics?.completed_shifts || 0}
                        <span className="unit-text">shifts</span>
                      </SummaryValue>
                    </SummaryContent>
                    <SummaryIconWrapper colorName="success">
                      <img src={CompletedIcon} alt="Completed Shifts" />
                    </SummaryIconWrapper>
                  </SummaryCard>

                  <SummaryCard>
                    <SummaryContent>
                      <SummaryTitle>Cancelled</SummaryTitle>
                      <SummaryValue>
                        {facilityShiftStatics?.cancelled_shifts || 0}
                        <span className="unit-text">shift</span>
                      </SummaryValue>
                    </SummaryContent>
                    <SummaryIconWrapper colorName="error">
                      <img src={CancelledIcon} alt="Cancelled Shifts" />
                    </SummaryIconWrapper>
                  </SummaryCard>
                </SummaryCardsContainer>
              </Box>

              <Box>
                <StyledTableContainer>
                  <StyledTable stickyHeader>
                    <CustomTableHeader
                      columns={FACILITY_SHIFT_COLUMNS}
                      order={order}
                      orderBy={orderBy as string}
                      onRequestSort={handleRequestSort}
                    />
                    <StyledTableBody>
                      {facilityShiftsLoading ? (
                        <TableRow>
                          <StyledTableCell colSpan={FACILITY_SHIFT_COLUMNS.length} align="center">
                            Loading...
                          </StyledTableCell>
                        </TableRow>
                      ) : facilityShiftsError || !facilityShifts || !facilityShifts.length ? (
                        <TableRow>
                          <StyledTableCell colSpan={FACILITY_SHIFT_COLUMNS.length} align="center">
                            No data available
                          </StyledTableCell>
                        </TableRow>
                      ) : (
                        facilityShifts.map((shift) => (
                          <TableRow
                            key={shift.shift_id}
                            hover
                            sx={{ cursor: "pointer" }}
                          >
                            <StyledTableCell>{shift.shift_id}</StyledTableCell>
                            <StyledTableCell>{dayjs(shift.start_date_time).format('DD/MM/YYYY')}</StyledTableCell>
                            <StyledTableCell>{dayjs(shift.start_date_time).format('hh:mm A')}</StyledTableCell>
                            <StyledTableCell>{dayjs(shift.end_date_time).format('hh:mm A')}</StyledTableCell>
                            <StyledTableCell>{shift.position_role}</StyledTableCell>
                            <StyledTableCell>{shift.workers_required}</StyledTableCell>
                            <StyledTableCell>{shift.shift_assignee_count} / {shift.workers_required}</StyledTableCell>
                            <StyledTableCell>{shift.assigned_workers}</StyledTableCell>
                            <StyledTableCell>
                              <StatusTag status={shift.status}>
                                {shift.status}
                              </StatusTag>
                            </StyledTableCell>
                          </TableRow>
                        ))
                      )}
                    </StyledTableBody>
                  </StyledTable>
                </StyledTableContainer>
                <PaginationFooter>
                  <Box>
                    Showing {facilityShifts?.length || 0} of {facilityShiftsCount} results
                  </Box>
                  <CustomPagination
                    currentPage={page}
                    totalPages={Math.ceil(facilityShiftsCount / rowsPerPage)}
                    onPageChange={(newPage) => setPage(newPage)}
                  />
                </PaginationFooter>
              </Box>

            </CustomTabPanel>
            <CustomTabPanel value="facility_details">
              <CustomCard>
                <CardTitle title='Basic Details' />
                <Grid container spacing={2}>
                  <Grid size={3}>
                    <BasicDetailItem
                      title='Facility Name'
                      value={facilityDetails?.facility_name} // Changed from workerDetails?.user.first_name
                    />
                    <BasicDetailItem
                      title='Contact Person'
                      value={facilityDetails?.contact_person} // Changed from workerDetails?.user.phone_number
                    />
                  </Grid>
                  <Grid size={3}>
                    <BasicDetailItem
                      title='Type'
                      value={facilityDetails?.facility_type_name} // Changed from workerDetails?.user.last_name
                    />
                    <BasicDetailItem
                      title='Designation'
                      value={facilityDetails?.contact_person_designation} // Changed from workerDetails?.user.email
                    />
                  </Grid>
                  <Grid size={3}>
                    <BasicDetailItem
                      title='ABN Number'
                      value={facilityDetails?.abn_number} // Changed from workerDetails?.gender
                    />
                    <BasicDetailItem
                      title='Phone Number'
                      value={facilityDetails?.phone_number} // Changed from workerDetails?.user.first_name + " " + workerDetails?.user.last_name
                    />
                  </Grid>
                  <Grid size={3}>
                    <BasicDetailItem
                      title='Email Address'
                      value={facilityDetails?.email} // Changed from workerDetails?.gender
                    />
                    <BasicDetailItem
                      title='Address'
                      value={facilityDetails?.address} // Changed from dayjs(workerDetails?.date_of_birth).format('DD/MM/YYYY')
                    />
                  </Grid>
                </Grid>
              </CustomCard>

              <CustomCard>
                <CardTitle title='Documents Uploaded' />


                {uploadedDocuments &&
                  uploadedDocuments.length > 0 &&
                  uploadedDocuments.map((document, index) => (
                    <DocsContainer key={document.document_id}>
                      <Grid container>
                        <Grid size={5}>
                          <Grid container spacing={2} wrap="nowrap">
                            <Grid sx={{ marginTop: 1 }}>
                              <img src={DocsIcon} alt="Document Icon" />
                            </Grid>
                            <Grid>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {document.document_name}
                              </Typography>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography
                                  variant='subtitle2'
                                  fontWeight="body2"
                                  title={getFileNameFromPath(document.document_url)}
                                  sx={{ cursor: 'pointer' }}
                                >
                                  {truncateLast(getFileNameFromPath(document.document_url))}
                                </Typography>
                                {document.is_accepted &&
                                  <CustomTooltip
                                    title={document.remarks}
                                    arrow
                                    placement="top"
                                  >
                                    <CheckCircleRoundedIcon color='primary' />
                                  </CustomTooltip>
                                }
                                {document.is_rejected &&
                                  <CustomTooltip
                                    title={document.remarks}
                                    arrow
                                    placement="top"
                                  >
                                    <CancelIcon color='error' />
                                  </CustomTooltip>
                                }
                              </Box>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid size={7}>
                          <Grid container>
                            <Grid size={6}>
                              <DateContainer>
                                <FormGroup>
                                  <FormLabel>
                                    Issued:
                                  </FormLabel>
                                  <Box sx={{ position: 'relative' }}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                      <DatePicker format="DD/MM/YYYY"
                                        enableAccessibleFieldDOMStructure={false}
                                        value={document.issuedDate}
                                        onChange={(newValue) => handleDocumentDateChange(index, 'issuedDate', newValue)}
                                        slots={{ textField: DateTextField }}
                                        slotProps={{
                                          textField: {
                                            variant: 'outlined',
                                            size: 'small',
                                          },
                                          popper: {
                                            disablePortal: true,
                                            sx: {
                                              position: 'absolute',
                                              top: '100%',
                                              left: '0',
                                              transform: 'none !important',
                                              zIndex: 1300,
                                            }
                                          }
                                        }}
                                      />
                                    </LocalizationProvider>
                                  </Box>
                                </FormGroup>
                                <FormGroup>
                                  <Typography variant="body2" sx={{ mr: 1, ml: 1, mt: 2 }}>
                                    to
                                  </Typography>
                                </FormGroup>
                                <FormGroup>
                                  <FormLabel>
                                    Expires:
                                  </FormLabel>
                                  <Box sx={{ position: 'relative' }}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                      <DatePicker format="DD/MM/YYYY"
                                        enableAccessibleFieldDOMStructure={false}
                                        value={document.expiresDate}
                                        onChange={(newValue) => handleDocumentDateChange(index, 'expiresDate', newValue)}
                                        slots={{ textField: DateTextField }}
                                        slotProps={{
                                          textField: {
                                            variant: 'outlined',
                                            size: 'small',
                                          },
                                          popper: {
                                            disablePortal: true,
                                            sx: {
                                              position: 'absolute',
                                              top: '100%',
                                              left: '0',
                                              transform: 'none !important',
                                              zIndex: 1300,
                                            }
                                          }
                                        }}
                                      />
                                    </LocalizationProvider>
                                  </Box>
                                </FormGroup>
                              </DateContainer>
                            </Grid>
                            <Grid size={6}>
                              <Box flexShrink={0} textAlign="right">
                                <Tooltip title="Preview">
                                  <IconButton
                                    onClick={() => {
                                      const fullUrl = document.document_url;
                                      setPreviewFileUrl(fullUrl);
                                      setPreviewFileType(getFileType(fullUrl));
                                      setPreviewFileName(document.document_name);
                                      setShowPreviewPopup(true);
                                    }}
                                  >
                                    <RemoveRedEyeIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Download">
                                  <IconButton
                                    onClick={() => {
                                      const fullUrl = document.document_url;
                                      handleDownload(fullUrl, getFileNameFromPath(fullUrl));
                                    }}
                                    color="primary"
                                  >
                                    <DownloadIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Accept">
                                  <span>
                                    <IconButton
                                      color="success"
                                      onClick={() => {
                                        setDocumentToAccept(document);
                                        setShowAcceptPopup(true);
                                      }}
                                      disabled={documentStatusUpdateLoading || document.is_accepted}
                                    >
                                      <CheckCircleOutlineIcon />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Reject">
                                  <span>
                                    <IconButton
                                      color="error"
                                      onClick={() => {
                                        setDocumentToReject(document);
                                        setShowRejectPopup(true);
                                      }}
                                      disabled={documentStatusUpdateLoading || document.is_rejected}
                                    >
                                      <ClearIcon />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </DocsContainer>
                  ))
                }


              </CustomCard>
            </CustomTabPanel>
          </TabContext>

        </PageCard>
      }

      <ConfirmationDialog
        open={confirmation.open}
        onClose={() => setConfirmation({ open: false, title: '', message: '', onConfirm: null })}
        onConfirm={confirmation.onConfirm || (() => { })}
        title={confirmation.title}
        message={confirmation.message}
      />

      <ActionPopup
        open={showSuspendPopup}
        onClose={() => setShowSuspendPopup(false)}
        onSubmit={(reason) => handleConfirmStatusUpdate('suspended', reason)}
        title="Suspend Facility"
        message="Are you sure you want to suspend this facility? Please provide a reason."
        submitButtonText="Suspend"
        textFieldLabel="Reason"
        textFieldPlaceholder="Enter reason for suspension"
        loading={updateStatusLoading}
        required={true}
      />
      <ActionPopup
        open={showRejectPopup}
        onClose={() => {
          setShowRejectPopup(false);
          setDocumentToReject(null);
        }}
        onSubmit={async (remark) => {
          if (!documentToReject || !facilityId) return;

          const result = await dispatch(acceptRejectFacilityDocument({
            documentId: documentToReject.document_id,
            action: 'reject',
            remarks: remark ?? '',
          }) as any);

          if (acceptRejectFacilityDocument.fulfilled.match(result)) {
            setAlert({ message: result.payload?.message || 'Document rejected successfully!', severity: 'success' });
            dispatch(fetchFacilityDetails(facilityId) as any); // Refresh facility details
            setShowRejectPopup(false);
            setDocumentToReject(null);
          } else {
            setAlert({ message: (result.payload as string) || 'Failed to reject document.', severity: 'error' });
          }
        }}
        title="Reject Document"
        message={`Are you sure you want to reject the document "${documentToReject?.document_name}"? Please provide a remark.`}
        submitButtonText="Reject"
        textFieldLabel="Rejection Remark"
        textFieldPlaceholder="Enter reason for rejection"
        loading={documentStatusUpdateLoading}
        required={true}
      />
      <ActionPopup
        open={showAcceptPopup}
        onClose={() => {
          setShowAcceptPopup(false);
          setDocumentToAccept(null);
        }}
        onSubmit={async (remark) => {
          if (!documentToAccept || !facilityId) return;

          const result = await dispatch(acceptRejectFacilityDocument({
            documentId: documentToAccept.document_id,
            action: 'accept',
            remarks: remark || '',
            document_issued_date: documentToAccept.issuedDate ? documentToAccept.issuedDate.format('YYYY-MM-DD') : undefined,
            document_expiring_date: documentToAccept.expiresDate ? documentToAccept.expiresDate.format('YYYY-MM-DD') : undefined,
          }) as any);

          if (acceptRejectFacilityDocument.fulfilled.match(result)) {
            setAlert({ message: result.payload?.message || 'Document accepted successfully!', severity: 'success' });
            dispatch(fetchFacilityDetails(facilityId) as any);
            setShowAcceptPopup(false);
            setDocumentToAccept(null);
          } else {
            setAlert({ message: (result.payload as string) || 'Failed to accept document.', severity: 'error' });
          }
        }}
        title="Accept Document"
        message={`Are you sure you want to accept the document "${documentToAccept?.document_name}"?`}
        submitButtonText="Accept"
        loading={documentStatusUpdateLoading}
        textFieldLabel="Acceptance Remark"
        textFieldPlaceholder="Enter remark for acceptance"
        required={false}
      />
      <DocumentPreviewPopup
        open={showPreviewPopup}
        onClose={() => setShowPreviewPopup(false)}
        fileUrl={previewFileUrl}
        fileType={previewFileType}
        fileName={previewFileName}
      />
    </Box>
  );
};

export default FacilityDetailsPage;
